import React, { FC, memo, useCallback, useState } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import BasicImage from '@home/components/BasicImage';
import { ESkillSourceType } from '../../../types';
import type { ISkillDetailHeaderProps } from '../../../types/skillDetail';
import { ESkillDetailTab } from '../../../types/skillDetail';
import {
  SKILL_DETAIL_ICONS,
  SKILL_DETAIL_TAB_CONFIG,
} from '../../../constants/skillDetail';
import SkillInstallDropdown from '../../../components/SkillInstallDropdown';
import SkillsDefaultIcon from '@/pages/aigc/assets/svg/skills-default-icon.svg';
import SkillMentionIcon from '@/pages/aigc/assets/svg/skill-mention.svg';
import './index.scss';

const PREFIX = 'dba-skill-detail-header';

/**
 * Skill 详情页顶部（Figma: skills集详情页顶部，含 Tab 栏）
 */
const SkillDetailHeader: FC<ISkillDetailHeaderProps> = ({
  display,
  skill,
  activeTab,
  onTabChange,
  onChatNow,
  onInstall,
  onDownload,
  onBack,
  isEN,
  isDownloading = false,
}) => {
  const { formatMessage } = useIntl();
  const breadcrumbText = formatMessage({ id: 'dba_skill_detail_back_title' });
  const [hoveredTabId, setHoveredTabId] = useState<ESkillDetailTab | null>(
    null,
  );

  const handleBack = useCallback((): void => {
    onBack();
  }, [onBack]);

  /** 是否支持立即对话：1=支持，缺省按 1 */
  const isDialogable = Number(skill.dialogable ?? 1) === 1;

  /** 立即对话：dialogable=0 时不跳转 */
  const handleChatNow = useCallback((): void => {
    if (!isDialogable) {
      return;
    }
    onChatNow();
  }, [isDialogable, onChatNow]);

  return (
    <div className={PREFIX}>
      <div className={`${PREFIX}__breadcrumb`}>
        <button
          type="button"
          className={`${PREFIX}__back`}
          onClick={handleBack}
        >
          <img
            src={SKILL_DETAIL_ICONS.back}
            alt=""
            className={`${PREFIX}__back-icon`}
          />
          <span className={`${PREFIX}__back-text`}>{breadcrumbText}</span>
        </button>
      </div>

      <div className={`${PREFIX}__body`}>
        <div className={`${PREFIX}__main`}>
          <div className={`${PREFIX}__icon`}>
            <BasicImage
              src={skill.category_icon || SkillsDefaultIcon}
              alt={display.displayName}
              errorSrc={SKILL_DETAIL_ICONS.defaultSkill}
            />
          </div>

          <div className={`${PREFIX}__info`}>
            <div className={`${PREFIX}__title-row`}>
              <h1 className={`${PREFIX}__title`}>{display.displayName}</h1>
              <div className={`${PREFIX}__tags`}>
                <span
                  className={classNames(`${PREFIX}__tag`, {
                    [`${PREFIX}__tag--official`]:
                      skill.source_type === ESkillSourceType.OFFICIAL,
                    [`${PREFIX}__tag--personal`]:
                      skill.source_type === ESkillSourceType.PERSONAL,
                  })}
                >
                  {display.sourceText}
                </span>
                {display.categoryText && (
                  <span
                    className={`${PREFIX}__tag ${PREFIX}__tag--category`}
                    style={display.categoryTagStyle}
                  >
                    {display.categoryText}
                  </span>
                )}
              </div>
            </div>

            <div className={`${PREFIX}__meta`}>
              <div className={`${PREFIX}__author`}>
                <BasicImage
                  src={display.authorAvatarSrc}
                  alt={display.authorDisplayName}
                  className={`${PREFIX}__avatar`}
                  errorSrc={SKILL_DETAIL_ICONS.authorDefault}
                />
                <span className={`${PREFIX}__author-name`}>
                  {display.authorDisplayName}
                </span>
              </div>
              <span className={`${PREFIX}__meta-divider`} />
              <span className={`${PREFIX}__meta-label`}>
                {formatMessage({ id: 'dba_skill_detail_usage_count' })}
              </span>
              <span className={`${PREFIX}__meta-value`}>
                {display.dialogCountText}
              </span>
              <span className={`${PREFIX}__meta-divider`} />
              <span className={`${PREFIX}__meta-label`}>
                {formatMessage({ id: 'dba_skill_detail_updated_at' })}
              </span>
              <span className={`${PREFIX}__meta-value`}>
                {display.updateTimeText}
              </span>
            </div>
          </div>

          <div className={`${PREFIX}__actions`}>
            {display.showKnot && display.knotLink && (
              <a
                className={`${PREFIX}__knot-btn`}
                href={display.knotLink}
                target="_blank"
                rel="noreferrer"
                title="Knot"
              >
                <img src={SKILL_DETAIL_ICONS.knot} alt="Knot" />
              </a>
            )}
            <SkillInstallDropdown
              skill={skill}
              isEN={isEN}
              isDownloading={isDownloading}
              onInstall={onInstall}
              onDownload={onDownload}
            />
            <button
              type="button"
              className={`${PREFIX}__chat-btn`}
              disabled={!isDialogable}
              onClick={handleChatNow}
            >
              <img
                src={SkillMentionIcon}
                alt=""
                className={`${PREFIX}__chat-btn-icon`}
              />
              <span>{formatMessage({ id: 'dba_skill_detail_chat_now' })}</span>
            </button>
          </div>
        </div>

        <div className={`${PREFIX}__tabs`} role="tablist">
          {SKILL_DETAIL_TAB_CONFIG.map((tab) => {
            const isActive = tab.id === activeTab;
            const isHovered = tab.id === hoveredTabId;
            const shouldShowActiveIcon = isActive || isHovered;

            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={classNames(`${PREFIX}__tab`, {
                  [`${PREFIX}__tab--active`]: isActive,
                })}
                onClick={() => onTabChange(tab.id)}
                onMouseEnter={() => setHoveredTabId(tab.id)}
                onMouseLeave={() => setHoveredTabId(null)}
              >
                <span className={`${PREFIX}__tab-label`}>
                  <img
                    src={
                      shouldShowActiveIcon ? tab.activeIcon : tab.defaultIcon
                    }
                    alt=""
                    className={`${PREFIX}__tab-icon`}
                  />
                  <span>{formatMessage({ id: tab.labelKey })}</span>
                </span>
                <span className={`${PREFIX}__tab-underline`} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default memo(SkillDetailHeader);
